// @ts-strict-ignore
import React, {
  useContext,
  useEffect,
  useMemo, useRef,
  useState,
} from 'react';
import {
  AdvancedMenuContent,
  Icon,
  IconName,
  useUniqueId,
} from '@usealloy/component-library';
import type { AdvancedMenuItemType } from '@usealloy/component-library';
import { StyledFilterTriggerButton, StyledFilterDropdownMenu } from './styles';
import type { OptionGroup } from './types';
import { AggregationEditorContext } from './context';

type AggregationDropdownOption = {
  value: string;
  label: string;
  [key: string]: unknown;
};

type AggregationSearchableDropdownProps = {
  groups: OptionGroup<AggregationDropdownOption>[];
  value?: string;
  placeholder?: string;
  /** Prefixes the trigger's label only, leaving the menu's own labels alone. */
  triggerPrefix?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  fallbackLabel?: string;
} & (
  | {
    mode: 'single';
    onSelect: (option: AggregationDropdownOption) => void;
  }
  | {
    mode: 'multi';
    selectedValues: string[];
    onToggle: (option: AggregationDropdownOption, checked: boolean) => void;
  }
);

function findLabelByValue(
  groups: OptionGroup<AggregationDropdownOption>[],
  value?: string,
): string | undefined {
  if (!value) return undefined;
  for (const group of groups) {
    const match = group.options.find(o => o.value === value);
    if (match) return match.label;
  }
  return undefined;
}

const AggregationSearchableDropdown = (props: AggregationSearchableDropdownProps) => {
  const {
    groups,
    mode,
    placeholder = '[select]',
    triggerPrefix,
    searchPlaceholder = 'Search',
    emptyMessage = 'No options available.',
    value,
    fallbackLabel,
    ...modeProps
  } = props;

  const { keyboardFocus } = useContext(AggregationEditorContext);

  const ref = useRef(null);
  const id = useUniqueId('aggregation-dropdown');

  useEffect(() => {
    keyboardFocus?.register({ id, ref });
    return () => keyboardFocus?.unregister(id);
  }, [id, keyboardFocus]);

  const onSelect = 'onSelect' in modeProps ? modeProps.onSelect : undefined;
  const onToggle = 'onToggle' in modeProps ? modeProps.onToggle : undefined;
  const selectedValues = 'selectedValues' in modeProps ? modeProps.selectedValues : undefined;

  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const selectedLabel = mode === 'single' ? findLabelByValue(groups, value) : undefined;
  const triggerLabel = selectedLabel
    ? [triggerPrefix, selectedLabel].filter(Boolean).join(' ')
    : (fallbackLabel ?? placeholder);

  const menuItems: AdvancedMenuItemType[] = useMemo(
    () => groups.map(group => ({
      type: 'group' as const,
      label: group.label,
      items: group.options.map(option => {
        if (mode === 'multi' && selectedValues && onToggle) {
          const isActive = selectedValues.includes(option.value);
          return {
            value: option.value,
            label: option.label,
            showCheckbox: true,
            checked: isActive,
            closeOnSelect: false,
            onCheckboxChange:
              (checked: boolean) => onToggle(option, checked),
            onSelect: () => onToggle(option, !isActive),
          };
        }
        return {
          value: option.value,
          label: option.label,
          onSelect: () => {
            if (onSelect) {
              onSelect(option);
            }
            setMenuIsOpen(false);
          },
        };
      }),
    })),
    [groups, mode, selectedValues, onToggle, onSelect],
  );

  if (groups.length === 0) return null;

  return (
    <StyledFilterDropdownMenu
      isOpen={menuIsOpen}
      onOpenChange={setMenuIsOpen}
      align="start"
      trigger={(
        <StyledFilterTriggerButton ref={ref}>
          {triggerLabel}
          <Icon icon={menuIsOpen ? IconName.CaretUp : IconName.CaretDown} size="small" />
        </StyledFilterTriggerButton>
      )}
    >
      <AdvancedMenuContent
        items={menuItems}
        enableMainFilter
        mainFilterPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        dynamicMenuHeight
        onClose={() => setMenuIsOpen(false)}
      />
    </StyledFilterDropdownMenu>
  );
};

export default AggregationSearchableDropdown;
